import _classCallCheck from "/vercel/path0/apps/corona/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/vercel/path0/apps/corona/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/createClass.js";
import _defineProperty from "/vercel/path0/apps/corona/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import * as R from 'ramda';
dayjs.extend(duration);
dayjs.extend(relativeTime);
/**
 * Returns a human-readable relative time string from a given date
 *
 * @param {string} date - The date to convert to a human-readable format
 * @returns {string} A human-readable string like "2 days ago", "1 month ago", etc.
 *
 * @example
 * // Returns something like "2 hours ago" depending on current time
 * getHumanizedDateFromNow('2023-03-01T10:00:00Z');
 */

export var getHumanizedDateFromNow = function getHumanizedDateFromNow(date) {
  return dayjs(date).fromNow();
};
/**
 * Configuration options for formatting age display
 *
 * @interface AgeFormatOptions
 * @property {('year'|'month'|'day')} precision - How detailed the age should be displayed
 * @property {boolean} hideZero - Whether to hide zero values
 * @property {string} delimiter - String to use between units (e.g., between years and months)
 * @property {Object} locale - Localization strings for units
 * @property {string} locale.year - String to use for years unit
 * @property {string} locale.month - String to use for months unit
 * @property {string} locale.day - String to use for days unit
 */

/**
 * A utility class for calculating and formatting age based on birth date
 *
 * @class AgeCalculator
 */
export var AgeCalculator = /*#__PURE__*/function () {
  function AgeCalculator(birthDate) {
    _classCallCheck(this, AgeCalculator);

    _defineProperty(this, "birthDate", void 0);

    _defineProperty(this, "defaultFormatOptions", {
      precision: 'year',
      hideZero: true,
      delimiter: ' ',
      locale: {
        year: 'y',
        month: 'm',
        day: 'd'
      }
    });

    this.birthDate = dayjs(birthDate);

    if (!this.birthDate.isValid()) {
      throw new Error('Invalid birth date provided');
    }
  }
  /**
   * Calculate age in years
   *
   * @param {string|Date} [referenceDate] - Optional date to calculate age from (defaults to current date)
   * @param {boolean} [float=false] - Whether to return age as a floating point number
   * @returns {number} Age in years
   * @throws {Error} If the provided reference date is invalid
   *
   * @example
   * // Assuming today is 2024-07-01
   * const calculator = new AgeCalculator('2000-01-01');
   *
   * // Get age as of today
   * const age = calculator.getAge(); // 24
   *
   * // Get age as of a specific date
   * const ageAsOf2020 = calculator.getAge('2020-01-01'); // 20
   *
   * // Get precise age with decimal
   * const preciseAge = calculator.getAge(undefined, true); // 24.5
   */


  _createClass(AgeCalculator, [{
    key: "getAge",
    value: function getAge(referenceDate) {
      var _float = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

      var reference = referenceDate ? dayjs(referenceDate) : dayjs();

      if (referenceDate && !reference.isValid()) {
        throw new Error('Invalid reference date provided');
      }

      return reference.diff(this.birthDate, 'year', _float);
    }
    /**
     * Calculate detailed age in years, months, and days
     *
     * @param {string|Date} [referenceDate] - Optional date to calculate age from (defaults to current date)
     * @returns {Object} Object containing years, months, and days components
     * @returns {number} returns.years - Full years component of age
     * @returns {number} returns.months - Months component of age (0-11)
     * @returns {number} returns.days - Days component of age
     * @throws {Error} If the provided reference date is invalid
     *
     * @example
     * const calculator = new AgeCalculator('2000-01-01');
     *
     * // Get detailed age as of a specific date
     * const detailedAge = calculator.getDetailedAge('2023-03-15');
     * // Returns: { years: 23, months: 2, days: 14 }
     */

  }, {
    key: "getDetailedAge",
    value: function getDetailedAge(referenceDate) {
      var reference = referenceDate ? dayjs(referenceDate) : dayjs();

      if (referenceDate && !reference.isValid()) {
        throw new Error('Invalid reference date provided');
      }

      var years = reference.diff(this.birthDate, 'year');
      var months = reference.diff(this.birthDate, 'month') % 12;
      var days = reference.diff(this.birthDate.clone().add(years, 'year').add(months, 'month'), 'day');
      return {
        years: years,
        months: months,
        days: days
      };
    }
    /**
     * Get age as formatted text according to specified options
     *
     * @param {string|Date} [referenceDate] - Optional date to calculate age from (defaults to current date)
     * @param {Partial<AgeFormatOptions>} [_options={}] - Formatting options
     * @returns {string} Formatted age string
     * @throws {Error} If the provided reference date is invalid
     *
     * @example
     * const calculator = new AgeCalculator('2014-01-31');
     *
     * // Basic usage - returns something like "8y"
     * calculator.getAgeText();
     *
     * // With reference date - returns "7y"
     * calculator.getAgeText('2022-01-30');
     *
     * // With month precision - returns "8y 0m"
     * calculator.getAgeText('2022-01-31', {
     *   precision: 'month',
     *   hideZero: false
     * });
     *
     * // With custom locale and delimiter - returns "7歲又10個月"
     * calculator.getAgeText('2021-12-30', {
     *   precision: 'month',
     *   locale: { year: '歲', month: '個月' },
     *   delimiter: '又'
     * });
     */

  }, {
    key: "getAgeText",
    value: function getAgeText(referenceDate) {
      var _options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

      var options = R.mergeDeepLeft(_options, this.defaultFormatOptions);
      var precision = options.precision,
          hideZero = options.hideZero,
          delimiter = options.delimiter;
      var locale = options.locale;
      var detailedAge = this.getDetailedAge(referenceDate);
      var year = detailedAge.years === 0 && hideZero ? '' : detailedAge.years + locale.year;
      var month = detailedAge.months === 0 && hideZero ? '' : detailedAge.months + locale.month;
      var day = detailedAge.days === 0 && hideZero ? '' : detailedAge.days + locale.day;

      switch (precision) {
        case 'day':
          {
            if (detailedAge.years === 0 && detailedAge.months === 0 && detailedAge.days === 0) {
              return 0 + locale.day;
            }

            var parts = [year, month, day].filter(function (part) {
              return part;
            });
            return parts.join(delimiter);
          }

        case 'month':
          {
            if (detailedAge.years === 0 && detailedAge.months === 0) {
              return 0 + locale.month;
            }

            var _parts = [year, month].filter(function (part) {
              return part;
            });

            return _parts.join(delimiter);
          }

        case 'year':
        default:
          {
            if (detailedAge.years === 0) {
              return 0 + locale.year;
            }

            return year;
          }
      }
    }
  }]);

  return AgeCalculator;
}();